/**
 * @module demo-dates-lib
 * @fileoverview Library to support webinar demo dates
 */

/**
 * Checks that the object Intl exists
 */
const hasIntl = window.Intl != null; // null || undefined
const hasIntlDateTimeFormat =
  hasIntl && typeof Intl.DateTimeFormat === "function";

// /////////////////////////////////////////////////////////////////////////////
// Filter function for days in the future
// /////////////////////////////////////////////////////////////////////////////

/**
 * @arg {Date} date
 * @return {Boolean}
 */
export function isUpcoming(date) {
  return date.getTime() > Date.now();
}

// /////////////////////////////////////////////////////////////////////////////
// Extract a sorted list of webinar dates
// /////////////////////////////////////////////////////////////////////////////

/**
 * @arg {string} string
 */
function pad0(string) {
  return string.length < 2 ? "0" + string : string;
}

/**
 * @arg {Date} date
 * @return {string}
 */
function makeKey(date) {
  const day = date.getUTCDay();
  const hours = date.getUTCHours().toString();
  const minutes = date.getUTCMinutes().toString();
  return [day, pad0(hours), pad0(minutes)].join("");
}

/**
 * @arg {Object.<string, Date>} acc
 * @arg {Date} date
 * @return {Object.<string, Date>}
 */
function indexDate(acc, date) {
  acc[makeKey(date)] = date;
  return acc;
}

/**
 * Extracts all unique days and times and sorts them by day of week.
 * @arg {Date[]} dates
 */
export function extractDateTimes(dates) {
  const dayTimeTable = dates.slice().reverse().reduce(indexDate, {});
  return Object.keys(dayTimeTable)
    .sort()
    .map(function (key) {
      return dayTimeTable[key];
    });
}

// /////////////////////////////////////////////////////////////////////////////
// Format webinar day + time
// /////////////////////////////////////////////////////////////////////////////

/**
 * Options to format day + time
 * @type {Intl.DateTimeFormatOptions}
 */
const dayTimeOpts = {
  weekday: "long",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
};

/**
 * @example
 * 1999-12-31T15:59:00.000Z -> 'Friday, 10:59 a.m. EST'
 * 2002-06-15T07:00:00.000Z -> 'Saturday, 3:00 a.m. EDT'
 * @arg {Date} date
 * @return {string}
 */
export function formatDayTime(date) {
  return hasIntlDateTimeFormat
    ? new Intl.DateTimeFormat(undefined, dayTimeOpts).format(date)
    : formatDayTimeFallback(date);
}

const DoW = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * @arg {Date} date
 */
function formatDayTimeFallback(date) {
  const day = DoW[date.getUTCDay()];
  const hours = date.getUTCHours().toString();
  const minutes = date.getUTCMinutes().toString();
  return day + ", " + pad0(hours) + ":" + pad0(minutes) + " GMT";
}

// /////////////////////////////////////////////////////////////////////////////
// Format for title
// /////////////////////////////////////////////////////////////////////////////

/**
 * @example
 * ['Single'] -> 'Single'
 * ['One', 'Two'] -> 'One and Two'
 * ['One', 'Two', 'Three', 'Many'] -> 'One, Two, Three and Many'
 * @arg {string[]} strings
 * @return {string}
 */
export function formatForTitle(strings) {
  const last = strings.pop() || "";
  if (strings.length < 1) return last;
  return strings.join(", ") + " and " + last;
}

// /////////////////////////////////////////////////////////////////////////////
// Format for drop-down
// /////////////////////////////////////////////////////////////////////////////

/**
 * Options to format date
 * @type {Intl.DateTimeFormatOptions}
 */
const dateOptsForText = {
  weekday: "long",
  month: "short",
  day: "numeric",
  year: "numeric",
};

/**
 * Options to format time
 * @type {Intl.DateTimeFormatOptions}
 */
const timeOptsForText = {
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "long",
};

/**
 * @example
 * Central Standard Time -> 'CST'
 * Pacific Daylight Time -> 'PDT'
 * GMT+10 -> 'GMT+10'
 * 2002-06-15T07:00:00.000Z -> '2002-06-15T07:00:00.000Z'
 * Formats a long form Time Zone Name and creates an Acronym using First letter of each word.
 * If tz starts with GMT or a number, just return it as is.
 * @arg {String} tz
 */
export const abbrTimeZone = (tz) => {
  // If TZ starts with GMT or a number, just return it.
  if (tz.match(/gmt|[0-9]/gi)) return tz;
  const matches = tz.match(/\b(\w)/g);
  if (Array.isArray(matches)) {
    // If the regex found a match, join them and return
    return matches.join("").toUpperCase();
  }
  return tz; // if no match, just return
};

let TZ = "America/Los_Angeles"; // UTC-0800 or UTC-0700
let dateOptsForValue = dateOptsForText;
let timeOptsForValue = timeOptsForText;
if (typeof Object.assign === "function") {
  /**
   * Options to format date
   * @type {Intl.DateTimeFormatOptions}
   */
  dateOptsForValue = Object.assign({ timeZone: TZ }, dateOptsForText);

  /**
   * Options to format time
   * @type {Intl.DateTimeFormatOptions}
   */
  timeOptsForValue = Object.assign({ timeZone: TZ }, timeOptsForText);
} else {
  TZ = "UTC";
  dateOptsForValue.timeZone = TZ;
  timeOptsForValue.timeZone = TZ;
}
/**
 * @example
 * 1999-12-31T15:59:00.000Z -> 'Friday, Dec. 31, 1999 — 10:59 a.m. EST'
 * 2002-06-15T07:00:00.000Z -> 'Saturday, Jun. 15, 2002 — 3:00 a.m. EDT'
 * Formats a Date object to a local aware string as used in form drop-downs.
 * @arg {Date} date
 * @return {String}
 */
function formatDateForSelectText(date) {
  let formatedDate;
  if (hasIntlDateTimeFormat) {
    const fd = new Intl.DateTimeFormat(undefined, dateOptsForText).format(date);
    const ft = new Intl.DateTimeFormat(undefined, timeOptsForText)
      .formatToParts(date)
      .map(({ type, value }) => {
        switch (type) {
          case "timeZoneName":
            return `${abbrTimeZone(value)}`;
          default:
            return value;
        }
      })
      .reduce((string, part) => string + part);
    formatedDate = fd + " — " + ft;
  } else formatedDate = date.toString();
  return formatedDate;
}

/**
 * @example
 * 1999-12-31T15:59:00.000Z -> 'Friday, Dec. 31, 1999 — 10:59 a.m. EST'
 * 2002-06-15T07:00:00.000Z -> 'Saturday, Jun. 15, 2002 — 3:00 a.m. EDT'
 * Formats a Date object to a local aware string as used in form drop-downs.
 * @arg {Date} date
 * @return {String}
 */
function formatDateForSelectValue(date) {
  let formatedDate;
  if (hasIntlDateTimeFormat) {
    const fd = new Intl.DateTimeFormat("en-US", dateOptsForValue).format(date);
    const ft = new Intl.DateTimeFormat("en-US", timeOptsForValue).format(date);
    formatedDate = fd + " — " + ft;
  } else formatedDate = date.toString();
  return formatedDate;
}

/**
 * @arg {Date} date
 * @return {{text: string, value: string}}
 */
export function formatDateForSelect(date) {
  return {
    text: formatDateForSelectText(date),
    value: formatDateForSelectValue(date),
  };
}

/**
 * @arg {String | Number} ts
 * @return {Date}
 */
export function timestampToDate(ts) {
  const unixTimestamp = typeof ts === "string" ? parseInt(ts, 10) : ts;
  const milliseconds = unixTimestamp * 1000;
  return new Date(milliseconds);
}

// /////////////////////////////////////////////////////////////////////////////
// Populate DOM select element
// /////////////////////////////////////////////////////////////////////////////

/**
 * @arg {HTMLSelectElement} select
 * @arg {{text: string, value: string}[]} slots
 * @arg {string} selectLabelOption
 * @return {void}
 */
export function updateSelectElm(select, slots, selectLabelOption = "") {
  select.value = "";
  if (slots && selectLabelOption !== "") {
    const opt = document.createElement("option");
    opt.value = "";
    opt.textContent = selectLabelOption;
    opt.disabled = true;
    opt.selected = true;
    select.appendChild(opt);
  }
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const opt = document.createElement("option");
    opt.value = slot.value;
    opt.textContent = slot.text;
    select.appendChild(opt);
  }
}

/**
 * @arg {Element | undefined} el
 * @arg {{text: string, value: string}[]} dates
 */
export function updateHTMLElementWithDates(el, dates) {
  if (el instanceof Element && el !== undefined) {
    for (let i = 0; i < dates.length; i++) {
      const newDiv = document.createElement("div");
      const newContent = document.createTextNode(dates[i].text);
      newDiv.appendChild(newContent);
      el?.appendChild(newDiv);
    }
  }
}
